import { CUTLIST_DOWNLOADABLE_ORDER_STATUS_OPTIONS } from '@cutr/constants/cutlist';
import cn from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Address,
  useAddressPretty,
  useDeliveryAddress,
  useIsAddressValid,
} from '@/api/address';
import { agentApi } from '@/api/backend/agent';
import { useCutlistState } from '@/api/store';
import { AddressInfo } from '@/blocks/AddressInfo';
import { AgentQuoteHeader } from '@/blocks/AgentOrderHeader';
import Card from '@/blocks/Card';
import { ConfirmationDialog } from '@/blocks/ConfirmationDialog';
import { OwnerInfo } from '@/blocks/OwnerInfo';
import { PriceSummary } from '@/blocks/PriceSummary';
import { Button } from '@/primitives/Button';
import { Field } from '@/primitives/Field';
import { ArrowLeft, Download, Icon } from '@/primitives/Icons';
import { Textarea } from '@/primitives/Input';
import {
  useAgentCutlistPatch,
  useAgentFinaliseQuote,
  useAgentUpdateCutlistAddress,
  useFinaliseOrder,
} from '@/queries/agent';
import { CutlistLoaderType } from '@/queries/loaders';
import { isStarterTier, useCurrentFeatures, useTicker } from '@/theme';
import { saveBlob } from '@/utils/file';

import { DeliveryAddressForm } from './AccountDetails';
import styles from './AgentQuoteCheckout.module.css';

const PATCH_DEBOUNCE_MS = 700;

const useDebouncedPatch = (id: string) => {
  const { mutate } = useAgentCutlistPatch(id);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return useCallback(
    (fields: Parameters<typeof mutate>[0]['fields']) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(
        () => mutate({ id, fields }),
        PATCH_DEBOUNCE_MS
      );
    },
    [id, mutate]
  );
};

export const AgentQuoteCheckout = () => {
  const isAddressValid = useIsAddressValid();
  const [isAddressEditing, setIsAddressEditing] = useState(!isAddressValid);
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { agentCheckoutFields } = useCurrentFeatures();

  const has = (field: (typeof agentCheckoutFields)[number]) =>
    agentCheckoutFields.includes(field);

  const handleBackToPricing = () => {
    navigate(`/admin/cutlists/orders/${id}/quote/price`);
  };

  return (
    <div>
      <AgentQuoteHeader />
      <section className="layout">
        {/* Main content */}
        <div className={styles.orderInfo}>
          <div className="flexAlign gap-m gutter">
            <Button
              className="outline smaller"
              icon={<Icon icon={<ArrowLeft />} />}
              onClick={handleBackToPricing}
            >
              {t('agent.orderCheckout.backToPricing')}
            </Button>
            <h2>{t('agent.orderCheckout.checkout')}</h2>
          </div>
          {has('erpReferenceField') && <ErpReferenceBlock />}
          {has('ownerField') && <OwnerBlock />}
          <DeliveryAddressBlock
            isAddressEditing={isAddressEditing}
            setIsAddressEditing={setIsAddressEditing}
          />
          {has('requestedDeliveryDateField') && <RequestedDeliveryDateBlock />}
          {has('deliverLeftoverMaterialsField') && <LeftoverMaterialBlock />}
          <form id="checkout-form">
            {has('customerReferenceField') && <CustomerReferenceBlock />}
            {has('internalPointOfContactField') && (
              <InternalPointOfContactBlock />
            )}
            {has('notesField') && <OrderNotesBlock />}
          </form>
        </div>

        <Aside finaliseQuoteDisabled={isAddressEditing || !isAddressValid} />
      </section>
    </div>
  );
};

const Aside = ({
  finaliseQuoteDisabled,
}: {
  finaliseQuoteDisabled: boolean;
}) => {
  const cutlist = useLoaderData() as CutlistLoaderType;
  const { t } = useTranslation();
  const { id } = useParams();
  const { mutateAsync: finaliseQuote } = useAgentFinaliseQuote();
  const { mutateAsync: finaliseOrder } = useFinaliseOrder();
  const { mutateAsync: patchCutlist } = useAgentCutlistPatch(id!);
  const { title, notes, customerReference, internalPointOfContact, shortId } =
    useCutlistState();
  const source = useTicker();
  const { sendCustomerEmail } = useCurrentFeatures();
  const [isFinaliseConfirmOpen, setIsFinaliseConfirmOpen] = useState(false);

  const isDownloadable = CUTLIST_DOWNLOADABLE_ORDER_STATUS_OPTIONS.includes(
    cutlist.status
  );

  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const form = document.getElementById('checkout-form') as HTMLFormElement;
    if (!form) return false;
    const isValid = form.checkValidity();

    if (!isValid) {
      form.reportValidity();
      return false;
    }

    return true;
  };

  const runFinaliseQuote = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      await finaliseQuote({ id });
      toast.success(t('agent.orderCheckout.finalizeQuoteSuccess'));
    } catch (error) {
      toast.error(t('agent.orderCheckout.finalizeQuoteError'));
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinaliseQuote = () => {
    if (!id || !validateForm()) return;

    if (sendCustomerEmail.includes('quote_ready')) {
      setIsFinaliseConfirmOpen(true);
      return;
    }

    runFinaliseQuote();
  };

  const handleMarkOrderPlaced = async () => {
    if (!id) return;
    setIsLoading(true);

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      await finaliseOrder({ id });
      toast.success(t('agent.orderCheckout.placeOrderSuccess'));
    } catch (error) {
      toast.error(t('agent.orderCheckout.placeOrderError'));
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadSap = async () => {
    if (!id) return;

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      await patchCutlist({
        id,
        fields: {
          title,
          notes,
          customerReference,
          internalPointOfContact,
        },
      });
      toast.success(t('common.cta.orderSaved'));

      const result = await agentApi.getCutlistSap(id);
      saveBlob(result, `${shortId}_sap.xlsx`);
    } catch (error) {
      toast.error(t('agent.toasts.fileDownloadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPricingPDF = async () => {
    if (!id) return;

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      await patchCutlist({
        id,
        fields: {
          title,
          notes,
          customerReference,
          internalPointOfContact,
        },
      });
      toast.success(t('common.cta.orderSaved'));

      const result = await agentApi.getCutlistPricingPDF(id);
      saveBlob(result, `${shortId}_pricing.pdf`);
    } catch (error) {
      toast.error(t('agent.toasts.fileDownloadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPricingSheet = async () => {
    if (!id) return;

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      await patchCutlist({
        id,
        fields: {
          title,
          notes,
          customerReference,
          internalPointOfContact,
        },
      });
      toast.success(t('common.cta.orderSaved'));

      const result = await agentApi.getCutlistPricingSheet(id);
      saveBlob(result, `${shortId}_pricing.xlsx`);
    } catch (error) {
      toast.error(t('agent.toasts.fileDownloadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const downloadZip = async () => {
    if (!id) return;

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      await patchCutlist({
        id,
        fields: {
          title,
          notes,
          customerReference,
          internalPointOfContact,
        },
      });
      const result = await agentApi.getCutlistZip(id);
      saveBlob(result, `${shortId}.zip`);
    } catch (error) {
      toast.error(t('agent.toasts.fileDownloadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const Buttons = () => {
    if (source === 'STIHO') {
      return (
        <>
          <Button
            variant="secondaryPill"
            className="full"
            style={{ fontWeight: 600 }}
            icon={<Icon icon={<Download />} />}
            onClick={downloadZip}
            tooltip={
              !isDownloadable
                ? t('agent.orderCheckout.downloadOrderDataTooltip')
                : undefined
            }
            disabled={isLoading || finaliseQuoteDisabled || !isDownloadable}
          >
            {t('agent.orderCheckout.downloadOrderData')}
          </Button>
          {cutlist.status !== 'order_placed' && (
            <Button
              onClick={handleFinaliseQuote}
              variant="secondary"
              className="full"
              disabled={isLoading || finaliseQuoteDisabled}
            >
              <strong>{t('agent.orderCheckout.finalizeQuote')}</strong>
            </Button>
          )}
          <Button
            onClick={handleMarkOrderPlaced}
            disabled={
              isLoading ||
              finaliseQuoteDisabled ||
              cutlist.status === 'order_placed'
            }
          >
            {t('agent.orderCheckout.markOrderPlaced')}
          </Button>
        </>
      );
    }

    if (isStarterTier()) {
      return (
        <>
          <Button
            variant="secondaryPill"
            className="full"
            style={{ fontWeight: 600 }}
            icon={<Icon icon={<Download />} />}
            onClick={downloadZip}
            tooltip={
              !isDownloadable
                ? t('agent.orderCheckout.downloadOrderDataTooltip')
                : undefined
            }
            disabled={isLoading || finaliseQuoteDisabled || !isDownloadable}
          >
            {t('agent.orderCheckout.downloadOrderData')}
          </Button>
          {cutlist.status !== 'order_placed' && (
            <Button
              onClick={handleFinaliseQuote}
              variant="secondary"
              className="full"
              disabled={isLoading || finaliseQuoteDisabled}
            >
              <strong>{t('agent.orderCheckout.finalizeQuote')}</strong>
            </Button>
          )}
          <Button
            onClick={handleMarkOrderPlaced}
            disabled={
              isLoading ||
              finaliseQuoteDisabled ||
              cutlist.status === 'order_placed'
            }
          >
            {t('agent.orderCheckout.markOrderPlaced')}
          </Button>
        </>
      );
    }

    if (source === 'SWISSKRONO') {
      return (
        <>
          <Button
            variant="secondaryPill"
            className="full"
            style={{ fontWeight: 600 }}
            icon={<Icon icon={<Download />} />}
            onClick={downloadPricingSheet}
            disabled={isLoading || finaliseQuoteDisabled}
          >
            {t('agent.orderCheckout.exportPricingSheet')}
          </Button>
          <Button
            variant="secondaryPill"
            className="full"
            style={{ fontWeight: 600 }}
            icon={<Icon icon={<Download />} />}
            onClick={downloadPricingPDF}
            disabled={isLoading || finaliseQuoteDisabled}
          >
            {t('agent.orderCheckout.exportPDF')}
          </Button>
          <Button
            variant="secondaryPill"
            className="full"
            style={{ fontWeight: 600 }}
            icon={<Icon icon={<Download />} />}
            onClick={downloadSap}
            disabled={isLoading || finaliseQuoteDisabled}
          >
            {t('agent.orderCheckout.exportSAP')}
          </Button>
          {cutlist.status !== 'order_placed' && (
            <Button
              onClick={handleFinaliseQuote}
              disabled={isLoading || finaliseQuoteDisabled}
            >
              {t('agent.orderCheckout.finalizeQuote')}
            </Button>
          )}
        </>
      );
    }

    if (source === 'CUTR') {
      return (
        <>
          <Button
            variant="secondaryPill"
            className="full"
            style={{ fontWeight: 600 }}
            icon={<Icon icon={<Download />} />}
            onClick={downloadZip}
            tooltip={
              !isDownloadable
                ? t('agent.orderCheckout.downloadOrderDataTooltip')
                : undefined
            }
            disabled={isLoading || finaliseQuoteDisabled || !isDownloadable}
          >
            {t('agent.orderCheckout.downloadOrderData')}
          </Button>
          {cutlist.status !== 'order_placed' && (
            <Button
              onClick={handleFinaliseQuote}
              disabled={isLoading || finaliseQuoteDisabled}
            >
              {t('agent.orderCheckout.finalizeQuote')}
            </Button>
          )}
        </>
      );
    }

    return (
      <>
        {cutlist.status !== 'order_placed' && (
          <Button
            onClick={handleFinaliseQuote}
            disabled={isLoading || finaliseQuoteDisabled}
          >
            {t('agent.orderCheckout.finalizeQuote')}
          </Button>
        )}
      </>
    );
  };

  return (
    <PriceSummary isReadOnly={true}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-s)',
          paddingTop: 'var(--space-m)',
        }}
      >
        <Buttons />
      </div>
      <ConfirmationDialog
        isOpen={isFinaliseConfirmOpen}
        title={t('agent.orderCheckout.finaliseQuoteEmailConfirm.title')}
        confirmLabel={t('agent.orderCheckout.finalizeQuote')}
        onClose={() => setIsFinaliseConfirmOpen(false)}
        onConfirm={() => {
          setIsFinaliseConfirmOpen(false);
          runFinaliseQuote();
        }}
      >
        {t('agent.orderCheckout.finaliseQuoteEmailConfirm.content')}
      </ConfirmationDialog>
    </PriceSummary>
  );
};

const OwnerBlock = () => {
  const { t } = useTranslation();
  return (
    <div className={'opposites'}>
      <h3>{t('agent.orderCheckout.owner')}</h3>
      <div className={styles.blockInfo}>
        <Card>
          <OwnerInfo />
        </Card>
      </div>
    </div>
  );
};

const DeliveryAddressBlock = ({
  isAddressEditing,
  setIsAddressEditing,
}: {
  isAddressEditing: boolean;
  setIsAddressEditing: (value: boolean) => void;
}) => {
  const deliveryInfo = useAddressPretty();
  const { t } = useTranslation();
  const {
    setProp: setDeliveryProp,
    init,
    ...deliveryAddress
  } = useDeliveryAddress();
  const { mutate: updateAddress } = useAgentUpdateCutlistAddress();
  const { id } = useParams();
  const [initialAddress, setInitialAddress] =
    useState<Address>(deliveryAddress);
  const isAddressValid = useIsAddressValid();
  const [isCancelEnabled, setIsCancelEnabled] = useState(isAddressValid);

  const handleSave = async () => {
    if (!id) return;
    try {
      updateAddress({ id, address: deliveryAddress });
      setInitialAddress(deliveryAddress);
      toast.success(t('agent.orderCheckout.deliveryAddressUpdated'));
    } catch (error) {
      toast.error(t('agent.orderCheckout.deliveryAddressUpdateError'));
    } finally {
      setIsAddressEditing(false);
      setIsCancelEnabled(true);
    }
  };
  return (
    <div className={'opposites'}>
      <h3>{t('agent.orderCheckout.deliveryTo')}</h3>
      <div className={styles.blockInfo}>
        {isAddressEditing ? (
          <Card>
            <form onSubmit={handleSave}>
              <DeliveryAddressForm
                title={''}
                address={deliveryAddress}
                setProp={setDeliveryProp}
              />
              <div
                style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end',
                  marginTop: '-1rem',
                }}
              >
                <Button type="submit" variant="straight">
                  {t('common.cta.save')}
                </Button>

                <Button
                  variant="secondary"
                  disabled={!isCancelEnabled}
                  onClick={() => {
                    init(initialAddress as Address);
                    setIsAddressEditing(false);
                  }}
                >
                  {t('common.cta.cancel')}
                </Button>
              </div>
            </form>
          </Card>
        ) : (
          <Card>
            <AddressInfo
              info={deliveryInfo}
              onEditAddress={() => setIsAddressEditing(true)}
            />
          </Card>
        )}
      </div>
    </div>
  );
};

const OrderNotesBlock = () => {
  const { notes, setNotes } = useCutlistState();
  const { t } = useTranslation();
  const source = useTicker();
  const { id } = useParams();
  const debouncedPatch = useDebouncedPatch(id!);

  useEffect(() => {
    if (source !== 'SWISSKRONO') return;

    if (notes === '') {
      const lines = [
        t('agent.orderCheckout.orderNotesDescription.line1'),
        t('agent.orderCheckout.orderNotesDescription.line2'),
        t('agent.orderCheckout.orderNotesDescription.line3'),
        t('agent.orderCheckout.orderNotesDescription.line4'),
        t('agent.orderCheckout.orderNotesDescription.line5'),
      ];
      setNotes(lines.join('\n'));
    }
  }, [notes, source, t, setNotes]);

  return (
    <div className={'opposites'}>
      <h3>{t('agent.orderCheckout.orderNotes')}</h3>
      <div className={styles.blockInfo}>
        <Textarea
          className="lighterBorder"
          style={{
            resize: 'vertical',
            minHeight: '10rem',
          }}
          name="notes"
          onInput={(e) => {
            setNotes(e.currentTarget.value);
            debouncedPatch({ notes: e.currentTarget.value });
          }}
          value={notes || ''}
          placeholder={t('review.notes.description')}
          rows={6}
        />
      </div>
    </div>
  );
};

const ErpReferenceBlock = () => {
  const { thirdPartyId, setThirdPartyId } = useCutlistState();
  const { t } = useTranslation();
  const { id } = useParams();
  const debouncedPatch = useDebouncedPatch(id!);
  return (
    <div className={'opposites'}>
      <h3>{t('agent.orderCheckout.erpReference')}</h3>
      <Field
        className={cn('fullWidth', 'lighterBorder')}
        label={''}
        name="thirdPartyId"
        type="text"
        setProp={(value) => {
          setThirdPartyId(value);
          debouncedPatch({ thirdPartyId: value });
        }}
        value={thirdPartyId || ''}
        maxLength={255}
      />
    </div>
  );
};

const CustomerReferenceBlock = () => {
  const { customerReference, setCustomerReference } = useCutlistState();
  const { t } = useTranslation();
  const { id } = useParams();
  const debouncedPatch = useDebouncedPatch(id!);
  return (
    <div className={'opposites'}>
      <h3>{t('agent.orderCheckout.customerReference')}</h3>
      <Field
        className={cn('fullWidth', 'lighterBorder')}
        label={''}
        name="customerReference"
        type="text"
        setProp={(value) => {
          setCustomerReference(value);
          debouncedPatch({ customerReference: value });
        }}
        value={customerReference || ''}
        maxLength={255}
      />
    </div>
  );
};

const InternalPointOfContactBlock = () => {
  const { internalPointOfContact, setInternalPointOfContact } =
    useCutlistState();
  const { t } = useTranslation();
  const { id } = useParams();
  const debouncedPatch = useDebouncedPatch(id!);

  return (
    <div className={'opposites'}>
      <h3>{t('agent.orderCheckout.internalPointOfContact')}</h3>
      <Field
        className={cn('fullWidth', 'lighterBorder')}
        label={''}
        name="internalPointOfContact"
        type="text"
        setProp={(value) => {
          setInternalPointOfContact(value);
          debouncedPatch({ internalPointOfContact: value });
        }}
        value={internalPointOfContact || ''}
        maxLength={255}
        isRequired={true}
      />
    </div>
  );
};

const RequestedDeliveryDateBlock = () => {
  const { requestedDeliveryDate, setRequestedDeliveryDate } = useCutlistState();
  const { t } = useTranslation();
  const { id } = useParams();
  const { mutate: patchCutlist } = useAgentCutlistPatch(id!);
  return (
    <div className={'opposites'}>
      <h3>{t('agent.orderCheckout.requestedDeliveryDate')}</h3>
      <Field
        className={cn('fullWidth', 'lighterBorder')}
        label={''}
        name="requestedDeliveryDate"
        type="date"
        setProp={(value) => {
          setRequestedDeliveryDate(value);
          patchCutlist({ id: id!, fields: { requestedDeliveryDate: value } });
        }}
        value={requestedDeliveryDate || ''}
      />
    </div>
  );
};

const LeftoverMaterialBlock = () => {
  const { deliverLeftoverMaterials } = useCutlistState();
  const { t } = useTranslation();
  return (
    <div className={'opposites'}>
      <h3>{t('review.leftoverMaterials.title')}</h3>
      <div className={styles.blockInfo}>
        <label className="flexAlign gap-s">
          <input
            type="checkbox"
            name="deliverLeftoverMaterials"
            checked={deliverLeftoverMaterials ?? false}
            disabled
          />
          {t('review.deliverLeftover.title')}
        </label>
      </div>
    </div>
  );
};
